import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Notification } from 'app/layout/common/notifications/notifications.types';
import { environment } from 'environment/environment';
import { catchError, EMPTY, map, Observable, ReplaySubject, Subscription, switchMap, take, tap, timer } from 'rxjs';
import { getAccessToken } from 'app/core/auth/access-token.store';

@Injectable({ providedIn: 'root' })
export class NotificationsService {
    private _notifications: ReplaySubject<Notification[]> = new ReplaySubject<Notification[]>(1);
    private apiURL = `${environment.API_notifications}/v1/notifications`;
    private notificationsPollingSub?: Subscription;
    private _deletedNotificationIds = new Set<string>();

    constructor(private _httpClient: HttpClient) { }

    get notifications$(): Observable<Notification[]> {
        return this._notifications.asObservable();
    }

    private getAuthHeaders(): HttpHeaders {
        const token = getAccessToken();
        return new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        });
    }

    startPolling(recipientId: number, intervalMs = 15000): void {
        this.stopPolling();
        this.notificationsPollingSub = timer(0, intervalMs)
            .pipe(switchMap(() => this.getAll(recipientId).pipe(
                catchError((err) => {
                    console.error('Notifications polling error:', err);
                    return EMPTY;
                })
            )))
            .subscribe({
                error: (err) => console.error('Notifications polling error:', err),
            });
    }

    stopPolling(): void {
        if (this.notificationsPollingSub) {
            this.notificationsPollingSub.unsubscribe();
            this.notificationsPollingSub = undefined;
        }
    }


    getAll(recipientId: number): Observable<Notification[]> {
        const headers = this.getAuthHeaders();
        return this._httpClient
            .get<any[]>(`${this.apiURL}/recipient/${recipientId}`, { headers })
            .pipe(
                map((notifications) => (notifications || []).map((n) => {
                        const notification: Notification = {
                            ...n,
                            read: n.read ?? false,
                        };

                        if (n.createdAt) {
                            notification.createdAt = new Date(n.createdAt);
                        }

                        if (n.sentAt) {
                            notification.sentAt = new Date(n.sentAt);
                        }

                        return notification;
                    }).filter((notification) => !this._deletedNotificationIds.has(String(notification.id)))),
                tap((normalized) => {
                    this._notifications.next(normalized);
                })
            );
    }

    create(notification: Notification): Observable<Notification> {
        return this.notifications$.pipe(
            take(1),
            switchMap((notifications) =>
                this._httpClient
                    .post<Notification>('api/common/notifications', { notification })
                    .pipe(
                        map((newNotification) => {
                            const normalized = {
                                ...newNotification,
                                createdAt: new Date(newNotification.createdAt),
                            };
                            this._notifications.next([...notifications, normalized]);
                            return normalized;
                        })
                    )
            )
        );
    }

    update(id: string | number, notification: Notification): Observable<Notification> {
        return this.notifications$.pipe(
            take(1),
            switchMap((notifications) =>
                this._httpClient
                    .patch<Notification>('api/common/notifications', { id, notification })
                    .pipe(
                        map((updatedNotification: Notification) => {
                            const index = notifications.findIndex((item) => item.id === id);
                            const normalized = {
                                ...updatedNotification,
                                createdAt: new Date(updatedNotification.createdAt),
                            };
                            if (index > -1) {
                                notifications[index] = normalized;
                            }
                            this._notifications.next(notifications);
                            return normalized;
                        })
                    )
            )
        );
    }

    delete(id: string | number): Observable<boolean> {
        return this._httpClient.delete<void>(`${this.apiURL}/${encodeURIComponent(String(id))}`, {
            headers: this.getAuthHeaders(),
        }).pipe(
            switchMap(() => {
                // Ignore stale polling/FCM responses after a successful deletion.
                this._deletedNotificationIds.add(String(id));
                return this.notifications$.pipe(
                    take(1),
                    tap((notifications) => this._notifications.next(
                        notifications.filter((item) => String(item.id) !== String(id))
                    )),
                    map(() => true)
                );
            })
        );
    }

    markAllAsRead(): Observable<boolean> {
        return this.notifications$.pipe(
            take(1),
            switchMap((notifications) =>
                this._httpClient
                    .get<boolean>('api/common/notifications/mark-all-as-read')
                    .pipe(
                        map((isUpdated: boolean) => {
                            const updated = notifications.map((n) => ({
                                ...n,
                                read: true,
                            }));
                            this._notifications.next(updated);
                            return isUpdated;
                        })
                    )
            )
        );
    }

    addFcmNotification(notification: any): void {
        if (this._deletedNotificationIds.has(String(notification.id))) {
            return;
        }
        this.notifications$.pipe(take(1)).subscribe((notifications) => {
            // Déjà reçue par la liste (rechargement ou push en double) : rien à ajouter.
            if (notifications.some((item) => String(item.id) === String(notification.id))) {
                return;
            }
            this._notifications.next([notification, ...notifications]);
        });
    }
}
